import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Avatar, Button, Input, Modal, toast, cn } from '@cyphex/ui';
import type { Conversation, ConversationId, Message, UserId } from '@cyphex/types';
import { chatApi } from '../../api';
import { previewText } from '../../lib/markdown/flatten';
import { viewOf } from '../../lib/conversationView';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface ForwardDialogProps {
  message: Message | null;
  conversations: readonly Conversation[];
  meId: UserId | null;
  onClose: () => void;
}

const MAX_TARGETS = 20;

/** Forward to up to 20 conversations, matching `POST /messages/:id/forward`. */
export function ForwardDialog({ message, conversations, meId, onClose }: ForwardDialogProps) {
  const { user: viewer } = useAuth();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ConversationId[]>([]);
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conversations
      .map((conversation) => ({ conversation, view: viewOf(conversation, meId, viewer) }))
      .filter(({ view }) => !needle || view.title.toLowerCase().includes(needle));
  }, [conversations, meId, query, viewer]);

  const toggle = (id: ConversationId): void => {
    setSelected((current) => {
      if (current.includes(id)) return current.filter((value) => value !== id);
      /* The API caps a forward at 20 targets — refuse rather than truncate later. */
      if (current.length >= MAX_TARGETS) return current;
      return [...current, id];
    });
  };

  const forward = async (): Promise<void> => {
    if (!message || selected.length === 0) return;
    setBusy(true);
    try {
      await chatApi.forwardMessage(message.conversationId, message.id, {
        conversationIds: selected,
      });
      toast.success(`Forwarded to ${selected.length} chat${selected.length === 1 ? '' : 's'}.`);
      setSelected([]);
      onClose();
    } catch {
      toast.error('Could not forward that message.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(message)}
      onOpenChange={(open) => !open && onClose()}
      title="Forward message"
      description="Choose where to send this message."
      size="md"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            {selected.length}/{MAX_TARGETS} selected
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" loading={busy} disabled={selected.length === 0} onClick={() => void forward()}>
              Forward
            </Button>
          </div>
        </div>
      }
    >
      {message ? (
        <p className="mb-3 line-clamp-2 rounded-lg border border-border bg-subtle p-2.5 text-sm text-muted-foreground">
          {previewText(message.bodyText ?? 'Attachment', 160)}
        </p>
      ) : null}

      <Input
        variant="search"
        size="sm"
        hideLabel
        label="Search chats"
        leadingIcon={Search}
        placeholder="Search chats"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <ul className="scrollbar-quiet mt-2 max-h-72 space-y-0.5 overflow-y-auto">
        {options.map(({ conversation, view }) => {
          const active = selected.includes(conversation.id);
          return (
            <li key={conversation.id}>
              <button
                type="button"
                onClick={() => toggle(conversation.id)}
                aria-pressed={active}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors duration-fast',
                  active ? 'bg-primary-subtle' : 'hover:bg-subtle',
                )}
              >
                <Avatar name={view.title} size="md" shape={view.isGroup ? 'tile' : 'circle'} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-md font-medium text-foreground">{view.title}</span>
                  <span className="block truncate text-sm text-muted-foreground">{view.subtitle}</span>
                </span>
                {active ? <Check size={16} strokeWidth={3} className="text-primary" aria-hidden /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
